<template>
  <b-row>
    <b-col>
      <chartjs-container title="Statistiques des appels par jour">
        <chartjs-component-bar-chart v-if="hasDailyData" :height="chartHeight" :chart-data="dailyChartData" />
        <p v-else>
          Aucune information disponible
        </p>
      </chartjs-container>
    </b-col>
    <b-col>
      <chartjs-container title="Statistiques des appels par heure">
        <chartjs-component-bar-chart v-if="hasHourlyData" :height="chartHeight" :chart-data="hourlyChartData" />
        <p v-else>
          Aucune information disponible
        </p>
      </chartjs-container>
    </b-col>
    <b-col>
      <chartjs-container :title="chartTitle">
        <chartjs-component-line-chart v-if="hasDayAndHourData" :height="chartHeight" :chart-data="chartData" />
        <p v-else>
          Aucune information disponible
        </p>
      </chartjs-container>
    </b-col>
  </b-row>
</template>

<script>
import {
  BCol,
  BRow,
} from 'bootstrap-vue'
import ChartjsContainer from '../ChartjsContainer.vue'
import ChartjsComponentBarChart from '../charts-components/ChartjsComponentBarChart.vue'
import ChartjsComponentLineChart from '../charts-components/ChartjsComponentLineChart.vue'
import useStatisticsRouteFetch from '../useStatisticsRouteFetch'
import {
  computedConfiguration,
  watchConfiguration,
  BASE_CHART_HEIGHT,
  chartHasMeaningfulData,
  CHART_COLORS,
} from './baseConfiguration'

export default {
  components: {
    BCol,
    BRow,
    ChartjsContainer,
    ChartjsComponentBarChart,
    ChartjsComponentLineChart,
  },
  data() {
    return {
      tabname: 'CallInflux',
      chartTitle: 'Affluence des appels par heure et jour',
      chartData: {},
      chartHeight: BASE_CHART_HEIGHT,
      dailyChartData: {},
      hourlyChartData: {},
    }
  },
  computed: {
    ...computedConfiguration,
    hasDailyData() {
      return chartHasMeaningfulData(this.dailyChartData)
    },
    hasHourlyData() {
      return chartHasMeaningfulData(this.hourlyChartData)
    },
    hasDayAndHourData() {
      return chartHasMeaningfulData(this.chartData)
    },
  },
  watch: watchConfiguration,
  methods: {
    fetchRouteOnActive() {
      if (this.active) {
        this.fetchRoute()
      }
    },
    async fetchRoute() {
      const response = await useStatisticsRouteFetch(0, 'CallInflux')
      this.buildDailyChart(response.callsByDay)
      this.buildHourlyChart(response.callsByHour)
      this.buildChart(response.callsByDayAndHour)
    },
    buildDailyChart(callsByDay) {
      if (!callsByDay || Object.keys(callsByDay).length === 0) {
        this.dailyChartData = {}
        return
      }

      // Check if data uses standard day names or special single day format
      const hasStandardDays = Object.keys(callsByDay).some(
        key => ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'].includes(key),
      )

      if (hasStandardDays) {
        // Standard week/month view with day names
        const days = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday']
        const dayTranslations = {
          Monday: 'Lundi',
          Tuesday: 'Mardi',
          Wednesday: 'Mercredi',
          Thursday: 'Jeudi',
          Friday: 'Vendredi',
          Saturday: 'Samedi',
          Sunday: 'Dimanche',
        }
        const values = days.map(day => callsByDay[day]?.Total || 0)

        if (values.every(v => v === 0)) {
          this.dailyChartData = {}
          return
        }

        this.dailyChartData = {
          labels: days.map(day => dayTranslations[day]),
          datasets: [{
            label: 'Appels reçus par jour',
            data: values,
            backgroundColor: CHART_COLORS.BRAND.ACCENT_RED,
            barPercentage: 0.9,
            categoryPercentage: 0.9,
            barThickness: 20,
          }],
        }
      } else {
        // Single day or year view - use actual keys from API
        const labels = Object.keys(callsByDay)
        const values = labels.map(label => callsByDay[label]?.Total || 0)

        if (values.every(v => v === 0)) {
          this.dailyChartData = {}
          return
        }

        this.dailyChartData = {
          labels,
          datasets: [{
            label: 'Appels reçus',
            data: values,
            backgroundColor: CHART_COLORS.BRAND.ACCENT_RED,
            barPercentage: 0.9,
            categoryPercentage: 0.9,
            barThickness: 20,
          }],
        }
      }
    },
    buildHourlyChart(callsByHour) {
      if (!callsByHour || Object.keys(callsByHour).length === 0) {
        this.hourlyChartData = {}
        return
      }

      const hours = Array.from({ length: 24 }, (_, i) => `${i.toString().padStart(2, '0')}h`)
      const data = hours.map(hour => callsByHour[hour]?.Total || 0)

      // Check if there's any meaningful data
      if (data.every(v => v === 0)) {
        this.hourlyChartData = {}
        return
      }

      this.hourlyChartData = {
        labels: hours,
        datasets: [{
          label: 'Appels par heure',
          data,
          backgroundColor: CHART_COLORS.BRAND.PRIMARY,
          barPercentage: 0.5,
          categoryPercentage: 0.7,
          barThickness: 20,
        }],
      }
    },

    buildChart(callsByDayAndHour) {
      if (!callsByDayAndHour || Object.keys(callsByDayAndHour).length === 0) {
        this.chartData = {}
        return
      }

      // Check if data uses standard day names or special format
      const actualDays = Object.keys(callsByDayAndHour)
      const hasStandardDays = actualDays.some(
        key => ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'].includes(key),
      )

      const allHours = Array.from({ length: 24 }, (_, i) => `${i.toString().padStart(2, '0')}h`)

      if (hasStandardDays) {
        // Standard week/month view
        const allDays = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday']
        const dayTranslations = {
          Monday: 'Lundi',
          Tuesday: 'Mardi',
          Wednesday: 'Mercredi',
          Thursday: 'Jeudi',
          Friday: 'Vendredi',
          Saturday: 'Samedi',
          Sunday: 'Dimanche',
        }

        const datasets = allDays.map((day, index) => {
          const hourlyData = allHours.map(hour => callsByDayAndHour?.[day]?.[hour] || 0)
          return {
            label: dayTranslations[day],
            data: hourlyData,
            borderColor: CHART_COLORS.PRIMARY[index],
            backgroundColor: CHART_COLORS.PRIMARY[index],
            fill: false,
          }
        })

        const hasData = datasets.some(dataset => dataset.data.some(value => value > 0))
        if (!hasData) {
          this.chartData = {}
          return
        }

        this.chartData = {
          labels: allHours,
          datasets,
        }
      } else {
        // Single day or year view - use actual keys from API
        const datasets = actualDays.map((day, index) => {
          const hourlyData = allHours.map(hour => callsByDayAndHour?.[day]?.[hour] || 0)
          return {
            label: day,
            data: hourlyData,
            borderColor: CHART_COLORS.PRIMARY[index % CHART_COLORS.PRIMARY.length],
            backgroundColor: CHART_COLORS.PRIMARY[index % CHART_COLORS.PRIMARY.length],
            fill: false,
          }
        })

        const hasData = datasets.some(dataset => dataset.data.some(value => value > 0))
        if (!hasData) {
          this.chartData = {}
          return
        }

        this.chartData = {
          labels: allHours,
          datasets,
        }
      }
    },
  },
}
</script>
